import React from "react"
import { useEffect, useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Autocomplete,
  TextField,
  TablePagination,
  Box,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import {
  ApiGetAllRoles,
  ApiGetShopperList,
  ApiPutUpdateShopperApplicationStatus,
} from "../../../apiService/Core/Apicall.tsx"
import { toast } from "react-toastify"
import { filterOptions, statusOptions } from "../../../global/constants/globalContants.ts"

const ShoppersList: React.FC = () => {
  const [shoppersList, setShoppersList] = useState<any>([])
  const [roles, setRoles] = useState<any>([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedFilter, setSelectedFilter] = useState<any>(null)
  const navigate = useNavigate()

  const navigateToViewShopperApplication = (shopper) => {
    navigate("/admin/shoppers/edit_shopper_profile", {
      state: { shopperDetails: shopper },
    })
  }

  useEffect(() => {
    const fetchData = async () => {
      await handleGetApiCallRoles()
      // Set default filter to "Active" on component mount
      const defaultFilter = filterOptions[0]
      setSelectedFilter(defaultFilter)
      await handleGetApiCall(defaultFilter.value)
    }
    fetchData()
    return () => {}
  }, [])

  const handleGetApiCallRoles = async () => {
    const data: any = {}
    try {
      const response: any = await ApiGetAllRoles(data)
      setRoles(response.data)
    } catch (error) {
      console.error("Error during API call:", error)
    }
  }

  const handleGetApiCall = async (status?: string) => {
    try {
      // Use the provided status or fall back to selectedFilter or default to 'active'
      const filterStatus = status || selectedFilter?.value || "active"
      const data = `?offset=0&limit=1000&status=${filterStatus}`
      const res = await ApiGetShopperList(data)
      setShoppersList(res.data)
    } catch (error) {
      console.error("Error during API call:", error)
    }
  }

  const handleFilterChange = async (event: any, newValue: any) => {
    setSelectedFilter(newValue)
    setPage(0) // Reset pagination to first page when filter changes
    if (newValue) {
      await handleGetApiCall(newValue.value)
    }
  }

  const handleStatusChange = async (event: any, newValue: any, userId: number) => {
    if (newValue) {
      try {
        const payload = {
          userId: userId,
          status: newValue.value,
        }
        const response: any = await ApiPutUpdateShopperApplicationStatus(payload)
        toast.success("Status updated successfully")
        await handleGetApiCall()
      } catch (error) {
        console.error("Error updating status:", error)
        toast.error("Failed to update status")
      }
    }
  }

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  // Get current page data
  const currentShoppers = shoppersList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <div className="manage-admin-container">
      <div className="manage-button">
        <h1 className="common-manage-h1">Shoppers List</h1>
      </div>

      {/* Filter Section */}
      <div style={{ width: "100%" }}>
        <Box sx={{ mb: 3,ml: 2, display: "flex", gap: 2, alignItems: "center", width: "fit-content" }}>
          <Autocomplete
            options={filterOptions}
            value={selectedFilter}
            getOptionLabel={(option) => option.label}
            onChange={handleFilterChange}
            renderInput={(params) => <TextField {...params} label="Filter by Status" />}
            sx={{ minWidth: 200 }}
          />
        </Box>
      </div>

      <Table>
        <TableHead>
          <TableRow className="table-row-background">
            {["First Name", "Last Name", "Email", "Status", "Edit"].map((item, index) => (
              <TableCell key={index} style={{ color: "#fff" }}>
                {item}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {currentShoppers?.map((user) => (
            <TableRow key={user.userId}>
              <TableCell>{user.firstName}</TableCell>
              <TableCell>{user.lastName}</TableCell>
              <TableCell>{user.emailId}</TableCell>
              <TableCell>
                <div className="w-200">
                  <Autocomplete
                    options={statusOptions}
                    value={statusOptions.find((option) => option.value === (user?.userStatus?.userStatus || ""))}
                    getOptionLabel={(option) => option.label}
                    onChange={(event, newValue) => handleStatusChange(event, newValue, user.userId)}
                    renderInput={(params) => <TextField {...params} label="Status" />}
                    fullWidth
                  />
                </div>
              </TableCell>
              <TableCell>
                <Button
                  variant="contained"
                  className="table-row-background"
                  onClick={() => navigateToViewShopperApplication(user)}
                >
                  Edit
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination component */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={shoppersList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  )
}

export default ShoppersList